import { ChangeDetectionStrategy, Component, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { LoginContainerComponent } from '@connect-services/client/components/login-container/login-container.component';
import { PasswordRulesVerificationComponent } from '@connect-services/client/components/password-rules-verification/password-rules-verification.component';
import { VersionComponent } from '@connect-services/client/components/version/version.component';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { AbstractSecurityComponent } from '@connect-services/client/pages/security/abstract-security/abstract-security.component';
import { CapitalizePipe } from '@connect-services/client/pipes/capitalize.pipe';
import { AuthSSOService } from '@connect-services/client/sdk/api';
import { environment } from '@connect-services/src/environments/environment';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';
import { SecurityFormField, SsoLoginFormGroup } from '@shared/client/models/form.model';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CapitalizePipe,
        LoadingComponent,
        LoginContainerComponent,
        PasswordRulesVerificationComponent,
        RessourcesModule,
        VersionComponent,
    ],
    selector: 'app-sso-login',
    styleUrl: '../abstract-security/abstract-security.component.scss',
    templateUrl: '../abstract-security/abstract-security.component.html',
})
export class SsoLoginComponent extends AbstractSecurityComponent implements OnInit {
    public override actionLabel = 'connectMe';
    public override fieldNames = [SecurityFormField.IDENTIFIER];
    public override readonly formGroup: WritableSignal<SsoLoginFormGroup> = signal<SsoLoginFormGroup>(
        {} as SsoLoginFormGroup,
    );
    public override hasBackNavigationButton = true;
    public override isLogin = false;
    public override title = 'loginToAccessYourOrganisation';

    private readonly authSsoService = inject(AuthSSOService);
    private readonly router = inject(Router);
    private readonly snackBarService = inject(SnackBarService);

    protected override initForm(): SsoLoginFormGroup {
        return new SsoLoginFormGroup({
            [SecurityFormField.IDENTIFIER]: new FormControl<string>('', {
                nonNullable: true,
                validators: [Validators.email, Validators.required],
            }),
        });
    }

    protected override async preInit(): Promise<void> {}

    protected override async submitAction(): Promise<void> {
        const formControls = this.formGroup().controls;
        const emailDomain = formControls[SecurityFormField.IDENTIFIER].value.split('@')[1];
        try {
            const response = await firstValueFrom(
                this.authSsoService.authSsoControllerGetOrganizationId({ emailDomain }),
            );

            window.location.assign(`${environment.baseApiUrl}/security/sso/${response.organizationId}/login`);
        } catch {
            this.snackBarService.openErrorSnackBar('noSsoConfigurationFound');

            await this.router.navigate(['/', RoutePathEnum.LOGIN]);
        }
    }
}
